import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  IconButton,
  CircularProgress,
  InputAdornment,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  ExitToApp as ExitToAppIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import { useGodMode } from '../../context/GodModeContext';

// Format milliseconds to mm:ss
const formatTimeRemaining = (ms: number | null): string | null => {
  if (!ms || ms <= 0) return null;
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const GodModePanel: React.FC = () => {
  const {
    isGodModeAvailable,
    isImpersonating,
    impersonatedUser,
    availableUsers,
    isLoading,
    isPanelOpen,
    sessionTimeRemaining,
    showTimeoutWarning,
    searchUsers,
    impersonate,
    exitGodMode,
    togglePanel,
  } = useGodMode();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch users when debounced query changes
  useEffect(() => {
    if (isGodModeAvailable && isPanelOpen && !isImpersonating) {
      searchUsers(debouncedQuery);
    }
  }, [debouncedQuery, isGodModeAvailable, isPanelOpen, isImpersonating, searchUsers]);

  const handleUserSelect = useCallback((userId: string) => {
    setSelectedUserId(prev => prev === userId ? null : userId);
  }, []);

  const handleStartImpersonation = useCallback(() => {
    if (selectedUserId) {
      impersonate(selectedUserId);
    }
  }, [selectedUserId, impersonate]);

  const handleExitClick = useCallback(() => {
    setShowExitConfirmation(true);
  }, []);

  const handleConfirmExit = useCallback(() => {
    setShowExitConfirmation(false);
    exitGodMode();
  }, [exitGodMode]);

  const handleCancelExit = useCallback(() => {
    setShowExitConfirmation(false);
  }, []);

  // Don't render if user doesn't have god mode privilege
  if (!isGodModeAvailable) {
    return null;
  }

  const timeDisplay = formatTimeRemaining(sessionTimeRemaining);

  // Minimized state - just show icon
  if (!isPanelOpen) {
    return (
      <Box
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 9999,
        }}
      >
        <IconButton
          onClick={togglePanel}
          sx={{
            backgroundColor: isImpersonating ? '#ff9800' : '#ffd700',
            color: '#000',
            '&:hover': {
              backgroundColor: isImpersonating ? '#f57c00' : '#ffcc00',
            },
            width: 56,
            height: 56,
            boxShadow: 3,
          }}
        >
          <Typography variant="h6">
            {isImpersonating ? '!' : '\u{1F451}'}
          </Typography>
        </IconButton>
      </Box>
    );
  }

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        width: 320,
        maxHeight: '70vh',
        zIndex: 9999,
        overflow: 'hidden',
        borderRadius: 2,
        border: isImpersonating ? '2px solid #ff9800' : '2px solid #ffd700',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: isImpersonating ? '#ff9800' : '#ffd700',
          color: '#000',
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {'\u{1F451}'} God Mode
          </Typography>
        </Box>
        <IconButton size="small" onClick={togglePanel}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2, maxHeight: 'calc(70vh - 60px)', overflow: 'auto' }}>
        {isImpersonating ? (
          // Impersonating view
          <Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Currently Impersonating:
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                {impersonatedUser?.displayName || impersonatedUser?.email}
              </Typography>
              <Chip
                size="small"
                label={impersonatedUser?.role || 'unknown'}
                color={impersonatedUser?.role === 'business' ? 'primary' : 'secondary'}
                sx={{ mt: 1 }}
              />
              {impersonatedUser?.businessName && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Business: {impersonatedUser.businessName}
                </Typography>
              )}
            </Box>

            {timeDisplay && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimerIcon fontSize="small" color={showTimeoutWarning ? 'error' : 'action'} />
                <Typography
                  variant="body2"
                  color={showTimeoutWarning ? 'error' : 'text.secondary'}
                  sx={{ fontWeight: showTimeoutWarning ? 'bold' : 'normal' }}
                >
                  Session expires in: {timeDisplay}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Button
              variant="contained"
              color="warning"
              fullWidth
              onClick={handleExitClick}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <ExitToAppIcon />}
            >
              Exit God Mode
            </Button>
          </Box>
        ) : (
          // User selection view
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Status: <Chip size="small" label="Ready" color="success" sx={{ ml: 1 }} />
            </Typography>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Select User to Impersonate:
            </Typography>

            <TextField
              fullWidth
              size="small"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={30} />
              </Box>
            ) : (
              <List
                sx={{
                  maxHeight: 250,
                  overflow: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 2,
                }}
              >
                {availableUsers.length === 0 ? (
                  <ListItem>
                    <ListItemText
                      secondary={searchQuery ? 'No users found' : 'Type to search users'}
                    />
                  </ListItem>
                ) : (
                  availableUsers.map((user: { _id: string; displayName?: string; email?: string; role?: string; businessName?: string }) => (
                    <ListItemButton
                      key={user._id}
                      selected={selectedUserId === user._id}
                      onClick={() => handleUserSelect(user._id)}
                    >
                      <ListItemIcon>
                        {user.role === 'business' || user.businessName ? (
                          <BusinessIcon color="primary" />
                        ) : (
                          <PersonIcon color="secondary" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={user.displayName || user.email}
                        secondary={
                          <span>
                            {user.role === 'business' || user.businessName
                              ? `Business${user.businessName ? `: ${user.businessName}` : ''}`
                              : 'Stylist'}
                          </span>
                        }
                      />
                    </ListItemButton>
                  ))
                )}
              </List>
            )}

            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleStartImpersonation}
              disabled={!selectedUserId || isLoading}
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              Start Impersonation
            </Button>
          </Box>
        )}
      </Box>

      {/* Exit Confirmation Dialog */}
      <Dialog
        open={showExitConfirmation}
        onClose={handleCancelExit}
        aria-labelledby="exit-confirmation-title"
        aria-describedby="exit-confirmation-description"
      >
        <DialogTitle id="exit-confirmation-title">
          Exit God Mode?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="exit-confirmation-description">
            Are you sure you want to exit impersonation mode? You are currently viewing the app as{' '}
            <strong>{impersonatedUser?.displayName || impersonatedUser?.email}</strong>.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelExit} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmExit} color="warning" variant="contained" autoFocus>
            Exit God Mode
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default GodModePanel;
